// Trusted Advisor System Types
// Comprehensive types for secure document sharing and management with Trusted Advisors

// ===== CORE TRUSTED ADVISOR TYPES =====

export type AdvisorStatus = 'pending' | 'active' | 'removed';
export type DocumentType = 
  | 'will' | 'trust' | 'power_of_attorney' | 'health_directive' | 'insurance'
  | 'financial_account' | 'investment' | 'retirement' | 'medical_record'
  | 'appointment' | 'medication' | 'pet_profile' | 'legacy_item' | 'digital_asset';

export type PermissionType = 'share_now' | 'share_after_death' | 'do_not_share';
export type ViewMode = 'view_only' | 'view_and_comment' | 'view_and_edit' | 'full_access';
export type UnlockRequestStatus = 'pending' | 'approved' | 'blocked' | 'unlocked' | 'expired';
export type InvitationStatus = 'pending' | 'accepted' | 'declined' | 'expired';
export type EmergencyContactAccessLevel = 'medical_emergency' | 'basic_contact' | 'limited_documents';
export type NotificationType = 
  | 'weekly_digest' | 'permission_change' | 'unlock_request' | 'access_granted'
  | 'access_revoked' | 'invitation_sent' | 'invitation_accepted';

export type AuditAction = 
  | 'advisor_added' | 'advisor_removed' | 'advisor_activated' | 'permission_granted'
  | 'permission_revoked' | 'permission_updated' | 'document_accessed' | 'document_viewed'
  | 'document_edited' | 'unlock_requested' | 'unlock_approved' | 'unlock_blocked'
  | 'unlock_completed' | 'invitation_sent' | 'invitation_accepted' | 'invitation_declined';

// ===== CORE INTERFACES =====

export interface TrustedAdvisor {
  id: string;
  user_id: string;
  first_name: string;
  last_name: string;
  email: string;
  relationship: string;
  status: AdvisorStatus;
  invited_at: string;
  activated_at?: string;
  removed_at?: string;
  created_at: string;
  updated_at: string;
}

export interface AdvisorPermission {
  id: string;
  advisor_id: string;
  document_id: string;
  document_type: DocumentType;
  permission_type: PermissionType;
  view_mode: ViewMode;
  unlocker_id?: string;
  unlock_wait_period_days: number;
  is_active: boolean;
  last_updated: string;
  created_at: string;
  updated_at: string;
}

export interface UnlockRequest {
  id: string;
  advisor_id: string;
  document_id: string;
  document_type: DocumentType;
  requested_at: string;
  status: UnlockRequestStatus;
  blocked_by_user: boolean;
  blocked_at?: string;
  unlocked_at?: string;
  unlock_expires_at?: string;
  created_at: string;
  updated_at: string;
}

export interface AdvisorAuditLog {
  id: string;
  user_id: string;
  advisor_id?: string;
  document_id?: string;
  document_type?: DocumentType;
  action: AuditAction;
  action_details: Record<string, any>;
  ip_address?: string;
  user_agent?: string;
  created_at: string;
}

export interface EmergencyContact {
  id: string;
  user_id: string;
  name: string;
  email?: string;
  phone?: string;
  relationship: string;
  access_level: EmergencyContactAccessLevel;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface AdvisorInvitation {
  id: string;
  user_id: string;
  advisor_id: string;
  invitation_token: string;
  invitation_message?: string;
  expires_at: string;
  accepted_at?: string;
  status: InvitationStatus;
  sent_at: string;
  created_at: string;
  updated_at: string;
}

export interface AdvisorNotification {
  id: string;
  user_id: string;
  advisor_id?: string;
  notification_type: NotificationType;
  title: string;
  message: string;
  is_read: boolean;
  sent_at: string;
  created_at: string;
}

// ===== REQUEST/REQUEST TYPES =====

export interface CreateTrustedAdvisorRequest {
  first_name: string;
  last_name: string;
  email: string;
  phone?: string;
  relationship: string;
  invitation_message?: string;
}

export interface UpdateTrustedAdvisorRequest {
  advisor_id: string;
  first_name?: string;
  last_name?: string;
  relationship?: string;
}

export interface CreateAdvisorPermissionRequest {
  advisor_id: string;
  document_id: string;
  document_type: DocumentType;
  permission_type: PermissionType;
  view_mode: ViewMode;
  unlocker_id?: string;
  unlock_wait_period_days?: number;
}

export interface UpdateAdvisorPermissionRequest {
  permission_id: string;
  permission_type?: PermissionType;
  view_mode?: ViewMode;
  unlocker_id?: string;
  unlock_wait_period_days?: number;
  is_active?: boolean;
}

export interface CreateUnlockRequestRequest {
  document_id: string;
  document_type: DocumentType;
}

export interface BlockUnlockRequestRequest {
  unlock_request_id: string;
  reason?: string;
}

export interface CreateEmergencyContactRequest {
  name: string;
  email?: string;
  phone?: string;
  relationship: string;
  access_level: EmergencyContactAccessLevel;
}

export interface UpdateEmergencyContactRequest {
  contact_id: string;
  name?: string;
  email?: string;
  phone?: string;
  relationship?: string;
  access_level?: EmergencyContactAccessLevel;
  is_active?: boolean;
}

export interface SendInvitationRequest {
  advisor_id: string;
  invitation_message?: string;
  expires_in_days?: number;
}

export interface AcceptInvitationRequest {
  invitation_token: string;
  user_id?: string; // For existing users
}

// ===== SUMMARY TYPES =====

export interface TrustedAdvisorSummary {
  total_advisors: number;
  active_advisors: number;
  pending_advisors: number;
  removed_advisors: number;
}

export interface AdvisorPermissionsSummary {
  advisor_id: string;
  user_id: string;
  first_name: string;
  last_name: string;
  email: string;
  relationship: string;
  advisor_status: AdvisorStatus;
  total_permissions: number;
  share_now_count: number;
  share_after_death_count: number;
  do_not_share_count: number;
}

export interface UnlockRequestSummary {
  id: string;
  advisor_id: string;
  document_id: string;
  document_type: DocumentType;
  status: UnlockRequestStatus;
  requested_at: string;
  blocked_by_user: boolean;
  unlocked_at?: string;
  unlock_expires_at?: string;
  first_name: string;
  last_name: string;
  email: string;
  unlock_wait_period_days: number;
  unlocker_id?: string;
}

export interface TrustedAdvisorDashboard {
  summary: TrustedAdvisorSummary;
  advisors: TrustedAdvisor[];
  permissions_summary: AdvisorPermissionsSummary[];
  pending_unlock_requests: UnlockRequestSummary[];
  emergency_contacts: EmergencyContact[];
  recent_activity: AdvisorAuditLog[];
  unread_notifications: number;
  pending_invitations_count: number;
}

// ===== PERMISSION MATRIX TYPES =====

export interface DocumentPermissionMatrix {
  document_id: string;
  document_name: string;
  document_type: DocumentType;
  advisor_permissions: {
    advisor_id: string;
    advisor_name: string;
    advisor_email: string;
    permission_type: PermissionType;
    view_mode: ViewMode;
    unlocker_id?: string;
    unlock_wait_period_days: number;
    is_active: boolean;
  }[];
}

export interface BulkPermissionUpdateRequest {
  document_id: string;
  advisor_permissions: {
    advisor_id: string;
    permission_type: PermissionType;
    view_mode: ViewMode;
    unlocker_id?: string;
    unlock_wait_period_days?: number;
  }[];
}

// ===== NOTIFICATION TYPES =====

export interface WeeklyDigestData {
  new_advisors: number;
  permission_changes: number;
  unlock_requests: number;
  document_accesses: number;
  recent_activity: AdvisorAuditLog[];
}

export interface PermissionChangeNotification {
  advisor_name: string;
  document_name: string;
  old_permission: PermissionType;
  new_permission: PermissionType;
  old_view_mode: ViewMode;
  new_view_mode: ViewMode;
}

export interface UnlockRequestNotification {
  advisor_name: string;
  document_name: string;
  document_type: DocumentType;
  requested_at: string;
  unlock_wait_period_days: number;
}

// ===== COMPONENT PROP TYPES =====

export interface TrustedAdvisorCardProps {
  advisor: TrustedAdvisor;
  permissions_summary?: AdvisorPermissionsSummary;
  onEdit: (advisor: TrustedAdvisor) => void;
  onRemove: (advisor_id: string) => void;
  onResendInvitation: (advisor_id: string) => void;
  onManagePermissions: (advisor_id: string) => void;
}

export interface PermissionMatrixProps {
  document_id: string;
  document_name: string;
  document_type: DocumentType;
  advisors: TrustedAdvisor[];
  permissions: AdvisorPermission[];
  onUpdatePermissions: (request: BulkPermissionUpdateRequest) => Promise<void>;
}

export interface UnlockPermissionModalProps {
  isOpen: boolean;
  onClose: () => void;
  document_id: string;
  document_name: string;
  document_type: DocumentType;
  advisor_id: string;
  current_permission: AdvisorPermission;
  onSave: (permission: UpdateAdvisorPermissionRequest) => Promise<void>;
}

export interface EmergencyContactCardProps {
  contact: EmergencyContact;
  onEdit: (contact: EmergencyContact) => void;
  onRemove: (contact_id: string) => void;
}

export interface TrustedAdvisorInvitationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onInvite: (request: CreateTrustedAdvisorRequest) => Promise<void>;
}

export interface AuditLogItemProps {
  log: AdvisorAuditLog;
  advisor?: TrustedAdvisor;
}

// ===== VALIDATION TYPES =====

export interface TrustedAdvisorValidationError {
  field: string;
  message: string;
  code: string;
}

export interface TrustedAdvisorValidationResult {
  isValid: boolean;
  errors: TrustedAdvisorValidationError[];
}

// ===== ANALYTICS TYPES =====

export interface TrustedAdvisorAnalytics {
  total_advisors: number;
  active_advisors: number;
  pending_invitations: number;
  documents_shared: number;
  unlock_requests_pending: number;
  unlock_requests_completed: number;
  permission_changes_this_month: number;
  most_shared_documents: Array<{
    document_name: string;
    document_type: DocumentType;
    share_count: number;
  }>;
  advisor_activity_timeline: Array<{
    date: string;
    advisors_added: number;
    permissions_changed: number;
    documents_accessed: number;
  }>;
  permission_distribution: Record<PermissionType, number>;
  view_mode_distribution: Record<ViewMode, number>;
}

// ===== EMAIL TEMPLATE TYPES =====

export interface InvitationEmailData {
  advisor_name: string;
  user_name: string;
  invitation_link: string;
  invitation_message?: string;
  expires_at: string;
}

export interface WeeklyDigestEmailData {
  user_name: string;
  digest_data: WeeklyDigestData;
  dashboard_link: string;
}

export interface UnlockRequestEmailData {
  user_name: string;
  advisor_name: string;
  document_name: string;
  document_type: DocumentType;
  requested_at: string;
  unlock_wait_period_days: number;
  block_link: string;
  approve_link: string;
}

export interface PermissionChangeEmailData {
  user_name: string;
  advisor_name: string;
  document_name: string;
  old_permission: PermissionType;
  new_permission: PermissionType;
  old_view_mode: ViewMode;
  new_view_mode: ViewMode;
  changed_at: string;
}

// ===== UTILITY TYPES =====

export interface TrustedAdvisorFilters {
  status?: AdvisorStatus;
  relationship?: string;
  has_permissions?: boolean;
  search?: string;
}

export interface AuditLogFilters {
  action?: AuditAction;
  advisor_id?: string;
  document_type?: DocumentType;
  date_from?: string;
  date_to?: string;
}

export interface UnlockRequestFilters {
  status?: UnlockRequestStatus;
  advisor_id?: string;
  document_type?: DocumentType;
  date_from?: string;
  date_to?: string;
}

// ===== CONSTANTS =====

export const PERMISSION_TYPE_LABELS: Record<PermissionType, string> = {
  share_now: 'Share Now',
  share_after_death: 'Share After Death',
  do_not_share: 'Do Not Share'
};

export const VIEW_MODE_LABELS: Record<ViewMode, string> = {
  view_only: 'View Only',
  view_and_comment: 'View & Comment',
  view_and_edit: 'View & Edit',
  full_access: 'Full Access'
};

export const DOCUMENT_TYPE_LABELS: Record<DocumentType, string> = {
  will: 'Will',
  trust: 'Trust',
  power_of_attorney: 'Power of Attorney',
  health_directive: 'Health Directive',
  insurance: 'Insurance',
  financial_account: 'Financial Account',
  investment: 'Investment',
  retirement: 'Retirement',
  medical_record: 'Medical Record',
  appointment: 'Appointment',
  medication: 'Medication',
  pet_profile: 'Pet Profile',
  legacy_item: 'Legacy Item',
  digital_asset: 'Digital Asset'
};

export const ADVISOR_STATUS_LABELS: Record<AdvisorStatus, string> = {
  pending: 'Pending',
  active: 'Active',
  removed: 'Removed'
};

export const UNLOCK_REQUEST_STATUS_LABELS: Record<UnlockRequestStatus, string> = {
  pending: 'Pending',
  approved: 'Approved',
  blocked: 'Blocked',
  unlocked: 'Unlocked',
  expired: 'Expired'
};

export const EMERGENCY_CONTACT_ACCESS_LABELS: Record<EmergencyContactAccessLevel, string> = {
  medical_emergency: 'Medical Emergency',
  basic_contact: 'Basic Contact',
  limited_documents: 'Limited Documents'
}; 